import classNames from 'classnames';
import {i18n} from 'mc/lib';
import {Select, SimpleDate, Textarea, Time} from 'mc/lib/field';
import {CompositeFocusBlur} from 'mc/lib/utils';
import {localUTCToDate} from 'mc/lib/utils/commons';
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import {Col, FormGroup, Label, Row} from 'reactstrap';
import getValue from 'redux-form/lib/events/getValue';
import './ObdobiField.scss';
import {obdobiPropType} from './utils';

// Typ obdobi
const DEN = 'den';
const OBDOBI = 'obdobi';

// Class names
const CLASS_NAME = 'obdobi-field';
const CLASS_NAME_TYP = CLASS_NAME + '-typ';
const CLASS_NAME_DATUM = CLASS_NAME + '-datum';
const CLASS_NAME_CAS = CLASS_NAME + '-cas';
const CLASS_NAME_UPRESNENI = CLASS_NAME + '-upresneni';
const CLASS_NAME_MINUS = CLASS_NAME + '-minus';
const CLASS_NAME_ITEMS = CLASS_NAME + '-items';
const CLASS_NAME_PREFIX = CLASS_NAME + '-prefix';

function detectTypFromProps(props) {
    const {datumPocatek, datumKonec} = props.value;
    return datumPocatek && datumKonec
        && localUTCToDate(datumPocatek).getTime() !== localUTCToDate(datumKonec).getTime()
         ? OBDOBI: DEN;
}

/**
 * Pole pro zadání SH období.
 */
export default class ObdobiField extends PureComponent {

    static propTypes = {
        inline: PropTypes.bool,
        value: obdobiPropType.isRequired,
        onChange: PropTypes.func,
    };

    static defaultProps = {
        inline: false,
    };

    constructor(props) {
        super(props);
        this.state = {typ: detectTypFromProps(props)};
        this.focusBlur = new CompositeFocusBlur(this);
    }

    componentDidUpdate() {
        // Přechod OBDOBI -> DEN ošetřit nemůžeme, protože by se to uživateli přepínalo i v některých
        // nechtěných případech. Museli bychom typ přenášet i v props, namísto toho ho držet ve state.
        if (this.state.typ === DEN && detectTypFromProps(this.props) === OBDOBI) {
            this.setState({typ: OBDOBI});
        }
    }

    handleTypChange = event => {
        const typ = event.target.value;
        if (typ === DEN) {
            this.handleChange({datumKonec: this.props.value.datumPocatek});
        }
        this.setState({typ});
    };

    handleDatumPocatekChange = datumPocatek => {
        const value = getValue(datumPocatek);
        if (this.state.typ === DEN) {
            this.handleChange({datumPocatek: value, datumKonec: value});
        } else {
            this.handleChange({datumPocatek: value});
        }
    };

    handleDatumKonecChange = datumKonec => {
        const value = getValue(datumKonec);
        this.handleChange({datumKonec: value});
    };

    handleCasPocatekChange = casPocatek => {
        this.handleChange({casPocatek});
    };

    handleCasKonecChange = casKonec => {
        this.handleChange({casKonec});
    };

    handleUpresnesniChange = event => {
        this.handleChange({upresneni: event.target.value});
    };

    handleChange(valueDiff) {
        const {onChange} = this.props;
        if (onChange) {
            onChange({...this.props.value, ...valueDiff});
        }
    }

    renderTypField() {
        const {inline} = this.props;
        const {typ} = this.state;
        return (
            <FormGroup>
                {inline && <Label>{i18n('obdobi.type')}</Label>}
                <Select value={typ}
                        onChange={this.handleTypChange}
                        className={CLASS_NAME_TYP}
                        {...this.focusBlur.handlers}>
                    <option key={DEN} value={DEN}>{i18n('obdobi.type.day')}</option>
                    <option key={OBDOBI} value={OBDOBI}>{i18n('obdobi.type.interval')}</option>
                </Select>
            </FormGroup>
        );
    }

    renderDatumField() {
        const {inline, value: {datumPocatek}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_DATUM}>
                {inline && <Label>{i18n('obdobi.day')}</Label>}
                <SimpleDate value={datumPocatek}
                            onChange={this.handleDatumPocatekChange}
                            {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }

    renderDatumPocatekField() {
        const {inline, value: {datumPocatek}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_DATUM}>
                {inline && <Label>{i18n('obdobi.startDate')}</Label>}
                <SimpleDate value={datumPocatek}
                            onChange={this.handleDatumPocatekChange}
                            {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }

    renderDatumKonecField() {
        const {inline, value: {datumKonec}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_DATUM}>
                {inline && <Label>{i18n('obdobi.endDate')}</Label>}
                <SimpleDate value={datumKonec}
                            onChange={this.handleDatumKonecChange}
                            {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }

    renderCasPocatekField() {
        const {inline, value: {casPocatek}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_CAS}>
                {inline && <Label>{i18n('obdobi.startTime')}</Label>}
                <Time value={casPocatek}
                      onChange={this.handleCasPocatekChange}
                       {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }

    renderCasKonecField() {
        const {inline, value: {casKonec}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_CAS}>
                {inline && <Label>{i18n('obdobi.endTime')}</Label>}
                <Time value={casKonec}
                      onChange={this.handleCasKonecChange}
                      {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }


    renderUpresneniField() {
        const {inline, value: {upresneni}} = this.props;
        return (
            <FormGroup className={CLASS_NAME_UPRESNENI}>
                {inline && <Label>Upřesnění</Label>}
                <Textarea placeholder={i18n('obdobi.upresneni')}
                          value={upresneni}
                          onChange={this.handleUpresnesniChange}
                          rows={inline ? 1 : 2}
                          {...this.focusBlur.handlers}/>
            </FormGroup>
        );
    }

    renderMinusSign() {
        return <div className={CLASS_NAME_MINUS}>-</div>;
    }

    render() {
        const {inline} = this.props;
        const {typ} = this.state;

        if (inline) {
            return (
                <div className={classNames(CLASS_NAME, 'inline')}>
                    <Row>
                        <Col xs={12} xl={6} className={CLASS_NAME_ITEMS}>
                            {this.renderTypField()}
                            {typ === DEN && this.renderDatumField()}
                            {typ === OBDOBI && this.renderDatumPocatekField()}
                            {this.renderCasPocatekField()}
                            {this.renderMinusSign()}
                            {typ === OBDOBI && this.renderDatumKonecField()}
                            {this.renderCasKonecField()}
                        </Col>
                        <Col xs={12} xl={6}>{this.renderUpresneniField()}</Col>
                    </Row>
                </div>
            )
        }

        return (
            <div className={CLASS_NAME}>
                <Row>
                    <Col xs={12} xl={4}>{this.renderTypField()}</Col>
                </Row>
                <Row>
                    {typ === DEN ? (
                        <Col xs={12} className={CLASS_NAME_ITEMS}>
                            {this.renderDatumField()}
                            {this.renderCasPocatekField()}
                            {this.renderMinusSign()}
                            {this.renderCasKonecField()}
                        </Col>
                    ) : (
                        <Col xs={12} xl={9}>
                            <div className={CLASS_NAME_ITEMS}>
                                <span className={CLASS_NAME_PREFIX}>{i18n('obdobi.start')}</span>
                                {this.renderDatumPocatekField()}
                                {this.renderCasPocatekField()}
                            </div>
                            <div className={CLASS_NAME_ITEMS}>
                                <span className={CLASS_NAME_PREFIX}>{i18n('obdobi.end')}</span>
                                {this.renderDatumKonecField()}
                                {this.renderCasKonecField()}
                            </div>
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col xs={12}>{this.renderUpresneniField()}</Col>
                </Row>
            </div>
        );
    }

}
